import { DEMO_HOSPITAL_STAFF } from "@/core/rh/demo-data";
import { loadHospitalStaff, staffFullName } from "@/core/rh/rh-api";
import type { HospitalStaffMember } from "@/core/rh/types";
import type { AppointmentPractitioner } from "./types";

const MEDICAL_POSITION =
  /médecin|medecin|doctor|chirurgien|cardiolog|pédiatre|pediatre|gynécolog|gynecolog|anesth/i;

/** Médecins démo complémentaires (registre local si RH indisponible). */
const EXTRA_DEMO_PRACTITIONERS: AppointmentPractitioner[] = [
  {
    id: "demo-prac-mukendi",
    displayName: "Dr. Jean Mukendi",
    department: "Médecine générale",
  },
  {
    id: "demo-prac-kabila",
    displayName: "Dr. Marie Kabila",
    department: "Cardiologie",
  },
  {
    id: "demo-prac-ilunga",
    displayName: "Dr. Paul Ilunga",
    department: "Pédiatrie",
  },
];

function isMedicalPractitioner(member: HospitalStaffMember): boolean {
  return member.status === "ACTIVE" && MEDICAL_POSITION.test(member.position);
}

function fromStaffMember(member: HospitalStaffMember): AppointmentPractitioner {
  const name = staffFullName(member);
  return {
    id: member.id,
    displayName: name.startsWith("Dr.") ? name : `Dr. ${name}`,
    department: member.department,
  };
}

function dedupeById(list: AppointmentPractitioner[]): AppointmentPractitioner[] {
  const seen = new Set<string>();
  return list.filter((p) => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
}

/** Liste synchrone (RH démo + registre local). */
export function listAppointmentPractitionersSync(): AppointmentPractitioner[] {
  const fromDemoStaff = DEMO_HOSPITAL_STAFF.filter(isMedicalPractitioner).map(fromStaffMember);
  return dedupeById([...fromDemoStaff, ...EXTRA_DEMO_PRACTITIONERS]).sort((a, b) =>
    a.displayName.localeCompare(b.displayName, "fr"),
  );
}

/** Charge le registre médecins (API RH si disponible, sinon démo). */
export async function loadAppointmentPractitioners(): Promise<AppointmentPractitioner[]> {
  try {
    const { staff } = await loadHospitalStaff();
    const medical = staff.filter(isMedicalPractitioner).map(fromStaffMember);
    if (medical.length > 0) {
      return dedupeById(medical).sort((a, b) =>
        a.displayName.localeCompare(b.displayName, "fr"),
      );
    }
  } catch {
    /* fallback démo */
  }
  return listAppointmentPractitionersSync();
}

export function getAppointmentPractitionerById(
  id: string,
  catalog?: AppointmentPractitioner[],
): AppointmentPractitioner | null {
  const list = catalog ?? listAppointmentPractitionersSync();
  return list.find((p) => p.id === id) ?? null;
}

export function practitionerDisplayName(
  practitionerId: string,
  fallbackName: string,
  catalog?: AppointmentPractitioner[],
): string {
  const found = getAppointmentPractitionerById(practitionerId, catalog);
  if (found) return found.displayName;
  return fallbackName.trim() || "—";
}
